// Backend encryption configuration - Must match Node.js backend exactly
const KEY_BASE64 = 'ZEOW3dwhndbGOcylqUTpERgQq/IXwNpRd70hg60iVdQ=';
const IV_BASE64 = 'JSyZX0WER8kDJ9G4';

/**
 * Convert base64 string to Uint8Array
 */
const base64ToUint8Array = (base64) => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

/**
 * Convert Uint8Array to base64 string
 */
const uint8ArrayToBase64 = (bytes) => {
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

/**
 * Encrypt message content using AES-256-GCM to match backend
 * @param {string} message - Plain text message
 * @returns {Promise<string>} - Encrypted message with auth tag (base64)
 */
export const encryptMessage = async (message) => {
  try {
    if (!message) return '';

    // Convert key and IV from base64
    const keyBytes = base64ToUint8Array(KEY_BASE64);
    const ivBytes = base64ToUint8Array(IV_BASE64);

    // Import key for Web Crypto API
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'AES-GCM' },
      false,
      ['encrypt']
    );

    // Convert message to Uint8Array
    const encoder = new TextEncoder();
    const messageBytes = encoder.encode(message);

    // Encrypt using AES-256-GCM
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: ivBytes
      },
      cryptoKey,
      messageBytes
    );

    // The encrypted buffer includes the auth tag at the end (last 16 bytes)
    const encryptedBytes = new Uint8Array(encryptedBuffer);

    // Return base64 encoded encrypted content with auth tag
    return uint8ArrayToBase64(encryptedBytes);

  } catch (error) {
    console.error('Encryption error:', error);
    return message; // Return original if encryption fails
  }
};

/**
 * Decrypt message content using AES-256-GCM to match backend
 * @param {string} encryptedMessage - Encrypted message (base64)
 * @returns {Promise<string>} - Decrypted plain text message
 */
export const decryptMessage = async (encryptedMessage) => {
  try {
    if (!encryptedMessage || typeof encryptedMessage !== 'string') {
      console.error('Invalid ciphertext provided for decryption:', encryptedMessage);
      return '';
    }

    // Convert key and IV from base64
    const keyBytes = base64ToUint8Array(KEY_BASE64);
    const ivBytes = base64ToUint8Array(IV_BASE64);

    console.log('🔑 Key length:', keyBytes.length, 'bytes (should be 32)');
    console.log('🔢 IV length:', ivBytes.length, 'bytes (should be 12)');
    console.log('📦 Encrypted message length:', encryptedMessage.length, 'chars');

    // Import key for Web Crypto API
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );

    // Convert encrypted message from base64 to Uint8Array
    const encryptedBytes = base64ToUint8Array(encryptedMessage);
    console.log('📊 Encrypted bytes length:', encryptedBytes.length, 'bytes (should be >= 16)');

    if (encryptedBytes.length < 16) {
      throw new Error(`Encrypted data too small: ${encryptedBytes.length} bytes (need at least 16 for auth tag)`);
    }

    // Decrypt using AES-256-GCM
    // The encrypted data includes the auth tag at the end
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: ivBytes
      },
      cryptoKey,
      encryptedBytes
    );

    // Convert decrypted buffer to string
    const decoder = new TextDecoder();
    const plainText = decoder.decode(decryptedBuffer);

    console.log('✅ Decryption successful:', plainText.substring(0, 50));
    return plainText;

  } catch (error) {
    console.error('❌ Decryption error:', error.message, error);
    return encryptedMessage; // Return encrypted if decryption fails
  }
};

/**
 * Check if a message is encrypted
 * @param {string} message
 * @returns {boolean}
 */
export const isEncrypted = (message) => {
  try {
    if (!message || typeof message !== 'string') return false;

    // Check if it's base64 encoded
    const base64Regex = /^[A-Za-z0-9+/=]+$/;
    return base64Regex.test(message);
  } catch {
    return false;
  }
};
